import { ErrorHandler, Injectable } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    AircraftRegisterEntryDetailsDto,
    ContactDto,
    ContactWithLocationDto,
    CraftService,
    CreateLocationCommand,
    DisplayableMissionDto,
    EquipmentDto,
    EquipmentService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    InUseMissionWorkflowVersionDto,
    LocationDetailsDto,
    LocationDto,
    LocationService,
    LockedMissionFields,
    MissionCrewDto,
    MissionNoticeDto,
    MissionRoleDto,
    MissionRoleService,
    MissionService,
    MissionWorkflowService,
    NotamDto,
    OrganisationAuthorityService,
    PersonRolesDto,
    PersonService,
    PersonnelRegisterEntryDetailsDto,
    RegisterEntryOnMissionSummary,
    RpaDto,
    RpaTypeDto,
    RpaTypesService,
    SetupGuideActivityDto,
    StateUpdateCommand,
    UpdateLocationCommand,
    UpdateMissionCommand,
    findActiveApprovals,
    observeFormControl,
    patchState,
    patchableState,
    prepareResponses,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { notEmptyArrayValidator } from '@flyfreely-portal-ui/validation';
import { WorkGroup } from 'libs/flyfreely/src/lib/services/workgroups.service';
import { SetupGuideChecklistService } from 'libs/onboarding/src/lib/setup-guide/setup-guide-checklist.service';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    firstValueFrom,
    of
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    mergeMap,
    startWith,
    takeUntil
} from 'rxjs/operators';
import { MissionFormModel } from '../interfaces';
import { validateRpaNotDummy } from '../mission-validators';
import { findWorkflowSelectionValidity } from './helpers';

export interface PersonWithRegisterStatus extends PersonRolesDto {
    disabled?: boolean;
    registerStatuses: PilotRegisterStatusUnion;
}

export type PilotRegisterStatusUnion =
    | null
    | 'LOADING'
    | { [authorityId: number]: PersonnelRegisterEntryDetailsDto.Status };

export interface SelectableRpa extends RpaDto {
    disabled: boolean;
    registerStatuses: PilotRegisterStatusUnion;
}

/**
 * A function that compares two register statuses and returns the most pressing/important status
 * It can be used in a reduce function to find the highest priority status from a list of statuses
 * @param status The current status to compare
 * @param currentMostPressing The currently most pressing status already determined, or a second status to compare against
 * @returns The more pressing/important of the two supplied statuses
 */
export function findMostPressingRegisterStatus(
    status:
        | AircraftRegisterEntryDetailsDto.Status
        | PersonnelRegisterEntryDetailsDto.Status,
    currentMostPressing:
        | AircraftRegisterEntryDetailsDto.Status
        | PersonnelRegisterEntryDetailsDto.Status
) {
    const type = AircraftRegisterEntryDetailsDto.Status;
    // Descending order of importance for status displays. Higher up = more important
    const order = [
        type.NOT_ON_REGISTER,
        type.REGISTER_DISABLED,
        type.NOT_AVAILABLE,
        type.SUSPENDED,
        type.EXPIRED,
        type.PENDING,
        type.ACTIVE
    ];
    // Use the order of importance above to return the most important of the two.
    if (order.indexOf(status) <= order.indexOf(currentMostPressing)) {
        return status;
    } else {
        return currentMostPressing;
    }
}

export function sortRpaByRegisterStatus(a: SelectableRpa, b: SelectableRpa) {
    /* const preferredOrder: string[] = [
        AircraftRegisterEntryDetailsDto.Status.NOT_ON_REGISTER,
        AircraftRegisterEntryDetailsDto.Status.NOT_AVAILABLE,
        AircraftRegisterEntryDetailsDto.Status.SUSPENDED,
        AircraftRegisterEntryDetailsDto.Status.EXPIRED,
        AircraftRegisterEntryDetailsDto.Status.PENDING,
        AircraftRegisterEntryDetailsDto.Status.ACTIVE,
        AircraftRegisterEntryDetailsDto.Status.AUTO
    ];
    if (
        preferredOrder.findIndex(x => x === aPrimaryStatus) >
        preferredOrder.findIndex(x => x === bPrimaryStatus)
    ) {
        return -1;
    } else if (
        preferredOrder.findIndex(x => x === aPrimaryStatus) <
        preferredOrder.findIndex(x => x === bPrimaryStatus)
    ) {
        return 1;
    } else {
        return 0;
    } */
    // FIXME: this sorting won't work with multiple registers and needs revision or removal.
    return 1;
}

export interface MissionObjectivesModel {
    name: FormControl<string>;
    projectId?: FormControl<number>;
    workgroup?: FormControl<WorkGroup>;
    isDummy: FormControl<boolean>;
    missionObjectiveTypeId?: FormControl<number>;
    missionObjective: FormControl<string>;
    location: FormControl<LocationDetailsDto>;
}
export interface MissionObjectivesValue {
    name: string;
    projectId?: number;
    workgroup?: WorkGroup;
    isDummy: boolean;
    missionObjectiveTypeId?: number;
    missionObjective: string;
    location: LocationDetailsDto;
}

/**
 * This service provides form and data management services for {@link MissionEditV2Dialogue} and its collaborators.
 *
 * This includes, providing lists of related entities, and building the mission edit forms.
 *
 * Data fetching is initiated by calling `setOrganisationId(...)` and form setup by `setup()`.
 */
@Injectable()
export class MissionEditService {
    private rpasSource = new ReplaySubject<RpaDto[]>();
    rpas$ = this.rpasSource.asObservable();

    private rpaTypesSource = new ReplaySubject<RpaTypeDto[]>();
    rpaTypes$ = this.rpaTypesSource.asObservable();

    private selectableRpasSource = new ReplaySubject<SelectableRpa[]>();
    selectableRpas$ = this.selectableRpasSource.asObservable();

    isSelectDummyRpa = new BehaviorSubject<boolean>(false);
    isSelectDummyRpa$ = this.isSelectDummyRpa.asObservable();

    private equipmentSource = new ReplaySubject<EquipmentDto[]>();
    equipment$ = this.equipmentSource.asObservable();

    private selectableEquipmentSource = new ReplaySubject<EquipmentDto[]>();
    selectableEquipment$ = this.selectableEquipmentSource.asObservable();

    isSelectDummyEquipment = new BehaviorSubject<boolean>(false);
    isSelectDummyEquipment$ = this.isSelectDummyEquipment.asObservable();

    get organisationId() {
        return this._organisationId;
    }

    private _organisationId: number;

    private savedMissionSource = new ReplaySubject<DisplayableMissionDto>();
    savedMission$ = this.savedMissionSource.asObservable();

    private _savedMission: DisplayableMissionDto;

    missionForm: FormGroup<MissionFormModel>;
    missionObjectivesGroup: FormGroup<MissionObjectivesModel>;

    private missionWorkflowsSource = new ReplaySubject<
        InUseMissionWorkflowVersionDto[]
    >(1);
    /**
     * The mission workflows available for the given operation type
     * on the given day. This is refreshed when the operation type
     * changes.
     */
    missionWorkflows$ = this.missionWorkflowsSource.asObservable();
    private missionWorkflows: InUseMissionWorkflowVersionDto[];

    private missionLocationSource = new ReplaySubject<LocationDetailsDto>(1);
    missionLocation$ = this.missionLocationSource.asObservable();
    private missionLocation: LocationDetailsDto;

    private missionDateSource = new ReplaySubject<Date>(1);
    missionDate$ = this.missionDateSource.asObservable();

    private locationsSource = new ReplaySubject<LocationDto[]>(1);
    locations$ = this.locationsSource.asObservable();

    private missionRolesSource = new ReplaySubject<MissionRoleDto[]>(1);
    missionRoles$ = this.missionRolesSource.asObservable();

    private loadingWorkflowSource = new BehaviorSubject<boolean>(false);
    loadingWorkflow$ = this.loadingWorkflowSource.asObservable();

    private registerEntriesOnMissionSource = new ReplaySubject<
        RegisterEntryOnMissionSummary[]
    >(1);
    registerEntriesOnMission$ =
        this.registerEntriesOnMissionSource.asObservable();

    private registerEntriesOnMissionLoadingSource =
        new BehaviorSubject<boolean>(false);
    registerEntriesOnMissionLoading$ =
        this.registerEntriesOnMissionLoadingSource.asObservable();

    private lockedFieldsSource = new ReplaySubject<
        StateUpdateCommand<LockedMissionFields>
    >(1);
    lockedFields$ = patchableState(this.lockedFieldsSource, {
        aircraft: false,
        isDummy: false,
        missionCrew: false,
        missionType: false,
        missionWorkflowVersion: false,
        timeZone: false
    });

    workflowSelectionValidity$ = combineLatest([
        this.savedMission$,
        this.missionWorkflows$
    ]).pipe(
        map(([mission, workflowList]) =>
            findWorkflowSelectionValidity(mission, workflowList)
        )
    );

    /**
     * True if the service was set up via `setupForReadOnly`.  When true,
     * subscriptions that would call organisation-scoped APIs (workflows,
     * register statuses) are skipped to avoid 403 errors for users who are
     * not members of the mission's organisation.
     */
    isReadOnly = false;

    private personnelRegisterStatus: {
        [personId: number]: Observable<PilotRegisterStatusUnion>;
    } = {};

    get missionId(): number | undefined {
        return this._savedMission?.id;
    }

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private missionWorkflowService: MissionWorkflowService,
        private craftService: CraftService,
        private rpaTypeService: RpaTypesService,
        private locationService: LocationService,
        private equipmentService: EquipmentService,
        private setupGuideChecklistService: SetupGuideChecklistService,
        private missionService: MissionService,
        private missionRoleService: MissionRoleService,
        private personService: PersonService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private errorHandler: ErrorHandler,
        private logging: FlyFreelyLoggingService
    ) {}

    ngOnDestroy() {
        this.lockedFieldsSource.complete();
        this.missionLocationSource.complete();
        this.missionWorkflowsSource.complete();
        this.savedMissionSource.complete();
        this.selectableRpasSource.complete();
        this.missionRolesSource.complete();
        this.loadingWorkflowSource.complete();
        this.missionDateSource.complete();
        this.selectableEquipmentSource.complete();
        this.equipmentSource.complete();
        this.registerEntriesOnMissionSource.complete();
        this.registerEntriesOnMissionLoadingSource.complete();
        this.isSelectDummyRpa.complete();
        this.isSelectDummyEquipment.complete();

        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * Kick off the feature
     * @param organisationId the organisation ID
     */
    setOrganisationId(organisationId: number) {
        this._organisationId = organisationId;
        this.craftService
            .findCrafts(this._organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpas => {
                const filteredRpas = rpas.filter(
                    rpa =>
                        rpa.status !== RpaDto.Status.RETIRED &&
                        rpa.status !== RpaDto.Status.DISPOSED &&
                        rpa.status !== RpaDto.Status.DELETED
                );
                this.rpasSource.next(filteredRpas);
            });

        this.rpaTypeService
            .findRpaTypes(organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(types => this.rpaTypesSource.next(types));

        this.locationService
            .findLocations(organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(locations => this.locationsSource.next(locations));

        this.missionRoleService
            .findMissionRoles(organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(roles => {
                this.missionRolesSource.next(roles);
            });

        this.equipmentService
            .find(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(equipment => {
                const filteredEquipment = equipment.filter(
                    eq =>
                        eq.status !== EquipmentDto.Status.RETIRED &&
                        eq.status !== EquipmentDto.Status.DISPOSED &&
                        eq.status !== EquipmentDto.Status.DELETED
                );
                this.equipmentSource.next(filteredEquipment);
            });
        this.setupMissionLocationSync();
    }

    /**
     * Set up the service for read-only access to a mission when the current user
     * is not a member of the mission's organisation.
     *
     * @param organisationId the organisation ID to use for any further API fetches
     * @param mission the mission being viewed, used to derive crew roles
     */
    setupForReadOnly(organisationId: number, mission: DisplayableMissionDto) {
        this._organisationId = organisationId;
        this.isReadOnly = true;

        // Derive unique roles from the mission's crew details.
        // The mission will be read only in this view so we only need a list of
        // roles already on the mission
        const rolesById = new Map<number, MissionRoleDto>();
        mission.missionCrewDetails?.forEach(crew => {
            if (crew.role != null) {
                rolesById.set(crew.role.id, crew.role);
            }
        });
        this.missionRolesSource.next(Array.from(rolesById.values()));

        // Populate data sources from the mission object where possible so that
        // display components have real data without making org-scoped API calls.
        this.equipmentSource.next(mission.equipment ?? []);

        this.locationsSource.next(
            mission.location != null
                ? ([mission.location] as unknown as LocationDto[])
                : []
        );

        this.rpasSource.next(
            (mission.craftIds ?? []).map(
                (id, index) =>
                    ({
                        id,
                        nickname: mission.craftNicknames?.[index] ?? '',
                        isDummy: false,
                        organisationId: mission.organisationId
                    } as unknown as RpaDto)
            )
        );

        // RPA types: not available on the mission DTO — emit empty so any
        // downstream combineLatest that includes rpaTypes$ can still resolve.
        this.rpaTypesSource.next([]);

        this.setupMissionLocationSync();
    }

    /**
     * Subscribes to mission location changes and keeps the form's location
     * control in sync. Shared by both `setOrganisationId` and `setupForReadOnly`.
     */
    private setupMissionLocationSync() {
        this.missionLocation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(location => {
                this.missionLocation = location;

                const initial =
                    this.missionForm.controls.missionObjectivesGroup.value
                        .location?.id;

                this.missionForm.controls.missionObjectivesGroup.patchValue({
                    location: location
                });

                if (initial !== location?.id) {
                    (<FormGroup<MissionObjectivesModel>>(
                        this.missionForm.controls.missionObjectivesGroup
                    )).controls.location.markAsDirty();
                }
            });
    }

    setup() {
        this.missionObjectivesGroup = this.fb.group<MissionObjectivesModel>({
            name: this.fb.control(undefined, [Validators.required]),
            workgroup: undefined,
            projectId: undefined,
            isDummy: this.fb.control(undefined, [Validators.required]),
            missionObjectiveTypeId: undefined,
            missionObjective: undefined,
            location: this.fb.control(undefined, [Validators.required])
        });

        this.missionForm = this.fb.group<MissionFormModel>({
            missionObjectivesGroup: this.missionObjectivesGroup,
            missionTypeId: this.fb.control(undefined, [Validators.required]),
            missionWorkflowVersionId: this.fb.control(undefined, [
                Validators.required,
                this.workflowConfirmed.bind(this),
                this.workflowApprovalStarted.bind(this)
            ]),
            additionalAuthorityIds: this.fb.array<number>([]),
            craftIds: this.fb.array<number>(
                [],
                [Validators.required, notEmptyArrayValidator()]
            ),
            equipmentIds: this.fb.array<number>([]),
            rpic: this.fb.control(undefined, [Validators.required]),
            missionEstimatedTime: undefined,
            missionCrew: this.fb.array<MissionCrewDto>([]),
            crewNotes: undefined,
            missionDate: undefined,
            timeZone: undefined,
            maximumHeight: undefined,
            aerodromesOfInterest: this.fb.array<ContactWithLocationDto>([]),
            emergencyContacts: undefined,
            missionEmergencyContacts: this.fb.array<ContactDto>([]),
            radioFrequencies: undefined,
            missionRadioFrequencies: this.fb.array<ContactDto>([]),
            visualLineOfSight: this.fb.control(undefined, [
                Validators.required
            ]),
            timeOfDay: this.fb.control(undefined, [Validators.required]),
            documentation: undefined,
            notams: this.fb.array<NotamDto>([]),
            declareNoRelevantNotam: undefined,
            lotams: this.fb.array<MissionNoticeDto>([]),
            pointOfContact: this.fb.control<string>(undefined, [
                Validators.maxLength(100)
            ]),
            pointOfContactInformation: this.fb.control<string>(undefined, [
                Validators.maxLength(100)
            ])
        });

        // In read-only mode, do not subscribe to missionTypeId changes to
        // trigger workflow refreshes — that would call findActiveWorkflows on an
        // organisation the user may not be a member of.
        if (!this.isReadOnly) {
            this.missionForm.controls.missionTypeId.valueChanges
                .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
                .subscribe(() => this.refreshMissionWorkflows());
        }

        this.missionForm.controls.missionDate.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(date => this.missionDateSource.next(date));

        if (!this.isReadOnly) {
            // In read-only mode this entire pipeline is skipped because
            // findRpaRegisterStatus calls organisationAuthorities APIs that
            // require membership of the mission's organisation.
            const rpaWithStatus = combineLatest([
                this.savedMission$,
                this.rpas$,
                this.organisationAuthorityService.change$.pipe(
                    startWith({ type: null, changedId: null })
                )
            ]).pipe(
                mergeMap(([mission, rpaList, changes]) =>
                    mission != null
                        ? this.findRpaRegisterStatus(
                              mission,
                              rpaList.map(p => p.id)
                          ).pipe(
                              catchError(() =>
                                  rpaList.map(p => ({
                                      ...p,
                                      disabled: true,
                                      registerStatuses: null
                                  }))
                              ),
                              map(registerStatuses => {
                                  const authorityIds =
                                      registerStatuses != null
                                          ? Object.keys(registerStatuses)
                                          : [];
                                  return rpaList.map(p => ({
                                      ...p,
                                      registerStatuses: authorityIds.reduce(
                                          (acc, id) => ({
                                              ...acc,
                                              [id]:
                                                  registerStatuses[id] != null
                                                      ? registerStatuses[id][
                                                            p.id
                                                        ]
                                                      : null
                                          }),
                                          {}
                                      )
                                  }));
                              }),
                              takeUntil(this.ngUnsubscribe$)
                          )
                        : of(
                              rpaList.map(
                                  p =>
                                      ({
                                          ...p,
                                          disabled: true,
                                          registerStatuses: null
                                      } as SelectableRpa)
                              )
                          )
                )
            );

            combineLatest([
                this.missionObjectivesGroup.controls.isDummy.valueChanges,
                this.missionForm.controls.craftIds.valueChanges.pipe(
                    distinctUntilChanged()
                ),
                rpaWithStatus
            ]).subscribe(([isDummy, rpaIds, rpas]) => {
                if (rpaIds == null || isDummy === true) {
                    this.selectableRpasSource.next(
                        rpas.map(r => ({
                            ...r,
                            disabled: false
                        }))
                    );
                } else {
                    this.selectableRpasSource.next(
                        rpas
                            .filter(
                                r =>
                                    rpaIds.indexOf(r.id) !== -1 ||
                                    r.isDummy === false
                            )
                            .map(r => ({
                                ...r,
                                disabled: r.isDummy
                            }))
                    );
                }
                this.missionForm.controls.craftIds.setValidators(
                    this.getCraftIdsValidator(isDummy, rpas)
                );
            });
        }

        combineLatest([
            this.missionObjectivesGroup.controls.isDummy.valueChanges,
            this.missionForm.controls.equipmentIds.valueChanges.pipe(
                distinctUntilChanged()
            ),
            this.equipment$
        ]).subscribe(([isDummy, equipmentIds, equipment]) => {
            if (equipmentIds == null || isDummy === true) {
                this.selectableEquipmentSource.next(
                    equipment.map(e => ({ ...e, disabled: false }))
                );
            } else {
                this.selectableEquipmentSource.next(
                    equipment
                        .filter(
                            e =>
                                equipmentIds.indexOf(e.id) !== -1 ||
                                e.isDummy === false
                        )
                        .map(e => ({ ...e, disabled: e.isDummy }))
                );
            }
        });

        // In read-only mode, skip the register-status lookups entirely.
        // findPersonnelRegisterStatusesForMission calls organisationAuthorities
        // APIs that require membership of the mission's organisation.
        if (!this.isReadOnly) {
            combineLatest([
                observeFormControl<{ rpicId: number; rpicRoleId: number }>(
                    this.missionForm.controls.rpic
                ),
                observeFormControl<MissionCrewDto[]>(
                    this.missionForm.controls.missionCrew
                ),
                observeFormControl<number[]>(
                    this.missionForm.controls.craftIds
                ),
                this.savedMission$.pipe(
                    startWith(null as DisplayableMissionDto)
                ),
                this.missionRoles$.pipe(startWith([] as MissionRoleDto[])),
                this.organisationAuthorityService.change$.pipe(
                    startWith({ type: null, changedId: null })
                )
            ])
                .pipe(
                    // Debounce to limit number of initial calls as observables populate on load
                    debounceTime(200),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe(
                    ([
                        rpic,
                        missionCrew,
                        rpaIds,
                        savedMission,
                        missionRoles,
                        changes
                    ]) => {
                        if (rpic?.rpicId == null || savedMission == null) {
                            return;
                        }
                        const pilotRole =
                            missionRoles != null && missionRoles.length > 0
                                ? missionRoles.find(
                                      roll =>
                                          roll.coreRole ===
                                          MissionRoleDto.CoreRole.PILOT
                                  )
                                : null;
                        const pilots =
                            pilotRole != null &&
                            missionCrew != null &&
                            missionCrew.length > 0
                                ? missionCrew?.filter(
                                      c => c.missionRoleId === pilotRole.id
                                  )
                                : [];
                        const missionPilots: number[] = [rpic.rpicId].concat(
                            pilots?.map(p => p.personId) ?? []
                        );
                        this.findMissionRegisterStatusesForPerson(
                            savedMission,
                            missionPilots,
                            rpaIds
                        );
                    }
                );
        }

        combineLatest([
            this.missionForm.controls.craftIds.valueChanges.pipe(startWith([])),
            this.selectableRpas$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([craftIds, rpas]) => {
                const hasDummyRpa = craftIds.some((id: number) => {
                    const rpa = rpas.find(r => r.id === id);
                    return rpa && rpa.isDummy;
                });
                this.isSelectDummyRpa.next(hasDummyRpa);
            });

        combineLatest([
            this.missionForm.controls.equipmentIds.valueChanges.pipe(
                startWith([])
            ),
            this.equipment$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([equipmentIds, equipment]) => {
                const hasDummyEquipment = equipmentIds.some((id: number) => {
                    const equipmentItem = equipment.find(e => e.id === id);
                    return equipmentItem && equipmentItem.isDummy;
                });
                this.isSelectDummyEquipment.next(hasDummyEquipment);
            });
    }

    set savedMission(mission: DisplayableMissionDto) {
        this._savedMission = mission;
        this.savedMissionSource.next(mission);
        this.missionLocationSource.next(mission.location);
        this.lockedFieldsSource.next({
            type: 'SET',
            data: mission.lockedFields
        });

        if (this.isReadOnly) {
            // In read-only mode only the workflow already on the mission is relevant.
            const workflowList =
                mission.missionWorkflowVersion != null
                    ? [mission.missionWorkflowVersion]
                    : [];
            this.missionWorkflows = workflowList;

            if (mission.missionWorkflowVersionId != null) {
                this.missionForm.controls.missionWorkflowVersionId.setValidators(
                    [Validators.required]
                );
                this.missionForm.controls.missionWorkflowVersionId.setValue(
                    mission.missionWorkflowVersionId
                );
            }

            this.missionWorkflowsSource.next(workflowList);

            // Build minimal SelectableRpa objects from the mission's own craftIds/craftNicknames arrays.
            const selectableRpas: SelectableRpa[] = (
                mission.craftIds ?? []
            ).map(
                (id, index) =>
                    ({
                        id,
                        nickname: mission.craftNicknames?.[index] ?? '',
                        isDummy: false,
                        disabled: false,
                        registerStatuses: null
                    } as unknown as SelectableRpa)
            );
            this.selectableRpasSource.next(selectableRpas);

            this.selectableEquipmentSource.next([]);
        }
    }

    /**
     * Change the mission location to the one provided. This may update the mission's location,
     * or create a new one if required
     */
    updateLocation(location: LocationDetailsDto) {
        if (
            this.missionLocation?.id === location?.id ||
            (this.missionLocation?.id == null &&
                this.missionLocation?.derivedFromId != null &&
                location.id != null &&
                location.type === LocationDetailsDto.Type.MISSION &&
                location.derivedFromId === this.missionLocation.derivedFromId)
        ) {
            this.setLocation(location);
        } else if (
            this.missionLocation != null &&
            this.missionLocation.id != null &&
            this.missionLocation.availableActions.canEdit
        ) {
            return this.updateMissionLocation(location);
        } else {
            return this.createMissionLocation(location);
        }
    }

    /**
     * Used to associate a new location with the mission, applying any
     * locking logic here, to mimic what the backend will do on save.
     * @param location the new location
     */
    setLocation(location: LocationDetailsDto) {
        this.missionLocationSource.next(location);

        if (location.timeZone != null) {
            this.lockedFieldsSource.next(patchState({ timeZone: true }));
        } else {
            this.lockedFieldsSource.next(patchState({ timeZone: false }));
        }
    }

    /**
     * Synchronises the workflow loading cycle across services and components.
     * Previously this was only housed in the mission edit component.
     * @param value the boolean value for "loadingWorkflow", indicating that a workflow change is in progress.
     */
    setLoadingWorkflowTracker(value: boolean) {
        this.loadingWorkflowSource.next(value);
    }

    /**
     * Validates that the workflow version is confirmed.
     * @param control the workflow version control
     */
    private workflowConfirmed(
        control: AbstractControl<number>
    ): ValidationErrors | null {
        return this._savedMission == null ||
            this._savedMission.id == null ||
            this._savedMission.missionWorkflowVersionId !== control.value
            ? { confirmed: false }
            : null;
    }

    /**
     * Validates that the workflow has a current approval.
     * @param control the workflow version control
     */
    private workflowApprovalStarted(
        control: AbstractControl<number>
    ): ValidationErrors | null {
        const workflowVersionId = control.value;

        return workflowVersionId != null &&
            this.missionWorkflows != null &&
            (this.missionWorkflows.find(wf => wf.id === workflowVersionId)
                ?.delegatedAuthority?.authorityType?.requiresApproval ||
                this.missionWorkflows
                    .find(wf => wf.id === workflowVersionId)
                    ?.additionalAuthorities.filter(a => a.requiresApproval)
                    .length > 0) &&
            findActiveApprovals(this._savedMission.approvals, true).length === 0
            ? { approval: false }
            : null;
    }

    private refreshMissionWorkflows() {
        const missionDate = this.missionForm.controls.missionDate.value;
        const timeZone = this.missionForm.controls.timeZone.value;

        const dateStr = toTimestamp(missionDate, timeZone);

        this.missionWorkflowService
            .findActiveWorkflows(this._savedMission.organisationId, dateStr)
            .subscribe({
                next: missionWorkflows => {
                    this.missionWorkflows = missionWorkflows;
                    this.missionWorkflowsSource.next(missionWorkflows);
                    this.missionForm.controls.missionWorkflowVersionId.updateValueAndValidity(
                        {
                            onlySelf: true
                        }
                    );
                },
                error: error => {
                    this.logging.error(error);
                }
            });
    }

    private getCraftIdsValidator(isDummy: boolean, rpas: RpaDto[]) {
        return isDummy
            ? Validators.required
            : Validators.compose([
                  Validators.required,
                  validateRpaNotDummy(rpas)
              ]);
    }

    private createMissionLocation(newLocation: LocationDetailsDto) {
        const command: CreateLocationCommand = {
            name: newLocation.name,
            features: newLocation.features,
            organisationId: this.organisationId,
            type: CreateLocationCommand.Type.MISSION,
            derivedFromId: newLocation.id ?? null
        };
        return this.locationService
            .createLocation(command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: location => this.setLocation(location),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while creating mission location: ${error.message}`
                    );
                }
            });
    }

    private updateMissionLocation(newLocation: LocationDetailsDto) {
        const command: UpdateLocationCommand = {
            name: newLocation.name,
            features: newLocation.features,
            derivedFromId:
                this.missionLocation.id !== newLocation.id
                    ? newLocation.id
                    : newLocation.derivedFromId
        };
        return this.locationService
            .updateLocation(this.missionLocation.id, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: location => {
                    this.setLocation(location);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating mission location: ${error.message}`
                    );
                }
            });
    }

    private saveOrGetLocationId() {
        const location = (<FormGroup<MissionObjectivesModel>>(
            this.missionForm.controls.missionObjectivesGroup
        )).controls.location.value;
        if (location == null) {
            return of(null);
        }
        if (location?.id != null) {
            return of(location.id);
        }

        const command: CreateLocationCommand = {
            name: location.name,
            features: location.features,
            organisationId: this.organisationId,
            type: CreateLocationCommand.Type.MISSION,
            derivedFromId: location.id ?? null
        };
        return this.locationService
            .createLocation(command)
            .pipe(map(l => l.id));
    }

    saveMission(): Promise<DisplayableMissionDto> {
        const initialValues = this.prepareForSaving();

        const step1 = firstValueFrom(
            this.saveOrGetLocationId().pipe(
                map(locationId => ({ ...initialValues, locationId }))
            )
        );

        if (this._savedMission.id != null) {
            return step1.then(forSaving =>
                this.missionService.updateMission(
                    this._savedMission.id,
                    forSaving
                )
            );
        } else {
            return step1
                .then(forSaving =>
                    this.missionService.createMission({
                        ...forSaving,
                        organisationId: this.organisationId,
                        modelVersion: this._savedMission?.modelVersion ?? 2,
                        declareNoRelevantNotam:
                            this.missionForm.controls.declareNoRelevantNotam
                                .value
                    })
                )
                .then(mission => {
                    this.setupGuideChecklistService.completeActivityTask(
                        SetupGuideActivityDto.StepIdentifier.CREATE_MISSION,
                        mission.id ?? null
                    );
                    return mission;
                });
        }
    }

    private prepareForSaving(): UpdateMissionCommand {
        // Validating mission crew. TODO this validation should be in a better place.
        const missionCrewValues = this.missionForm.controls.missionCrew.value;
        const missionCrew: MissionCrewDto[] =
            missionCrewValues != null
                ? missionCrewValues.filter(
                      pilot =>
                          pilot.personId !== null &&
                          pilot.missionRoleId !== null
                  )
                : [];

        // Strip out null rpa and equipment caused by adding an input row, but not selecting an RPA or equipment
        const missionRpaIds = this.missionForm.controls.craftIds.value.filter(
            id => id != null
        );
        const missionEquipmentIds =
            this.missionForm.controls.equipmentIds.value.filter(
                id => id != null
            );

        const rpic = this.missionForm.controls.rpic.value;
        if (rpic?.rpicId != null) {
            missionCrew.push({
                missionRoleId: rpic.rpicRoleId,
                personId: rpic.rpicId
            });
        }

        // We have to do type conversions to pass the UI values to the service
        const value = this.missionForm.value;
        return {
            name: value.missionObjectivesGroup.name,
            projectId: value.missionObjectivesGroup.projectId,
            missionObjectiveTypeId:
                value.missionObjectivesGroup.missionObjectiveTypeId,
            workgroupId: value.missionObjectivesGroup.workgroup?.id,
            missionObjective: value.missionObjectivesGroup.missionObjective,
            locationId: value.missionObjectivesGroup.location?.id,
            isDummy: value.missionObjectivesGroup.isDummy,
            missionDate: toTimestamp(value.missionDate, value.timeZone),
            timeZone: value.timeZone,
            missionTypeId: value.missionTypeId,
            missionEstimatedTime: value.missionEstimatedTime,
            craftIds: missionRpaIds,
            equipmentIds: missionEquipmentIds,
            missionWorkflowVersionId: value.missionWorkflowVersionId,
            additionalAuthorityIds: value.additionalAuthorityIds,
            maximumHeight: value.maximumHeight,
            crewNotes: value.crewNotes,
            missionCrew: missionCrew,
            emergencyContacts:
                this._savedMission.modelVersion === 1
                    ? value.emergencyContacts
                    : null,
            radioFrequencies:
                this._savedMission.modelVersion === 1
                    ? value.radioFrequencies
                    : null,
            missionContacts: value.missionEmergencyContacts,
            missionRadioFrequencies: value.missionRadioFrequencies,
            aerodromesOfInterest: value.aerodromesOfInterest.map(
                a => a.identifier
            ),
            visualLineOfSight: value.visualLineOfSight,
            timeOfDay: value.timeOfDay,
            formResponses:
                value.documentation != null
                    ? prepareResponses(value.documentation.formResponses)
                    : {},
            notams: value.notams.map(n => ({
                id: n.id,
                location: n.location,
                fir: n.fir
            })),
            pointOfContact: value.pointOfContact || null,
            pointOfContactInformation: value.pointOfContactInformation || null
        };
    }

    private findAuthorityIds(savedMission: DisplayableMissionDto) {
        const missionWorkflow = this.missionWorkflows?.find(
            workflow =>
                this.missionForm.controls.missionWorkflowVersionId.value ===
                workflow.id
        );
        const missionWorkflowAuthorityIds =
            missionWorkflow != null
                ? [missionWorkflow.delegatedAuthorityId].concat(
                      missionWorkflow.additionalAuthorityIds
                  )
                : savedMission?.missionWorkflowVersion != null
                ? [
                      savedMission.missionWorkflowVersion.delegatedAuthorityId
                  ].concat(
                      savedMission.missionWorkflowVersion.additionalAuthorityIds
                  )
                : [];
        const additionalAuthorityIds =
            this.missionForm.controls.additionalAuthorityIds.value ??
            savedMission?.additionalAuthorities.map(a => a.id) ??
            [];
        return missionWorkflowAuthorityIds.concat(additionalAuthorityIds);
    }

    /**
     * Resolve the register status for a person based on a concrete mission
     * @param savedMission
     * @param personIdList
     */
    private findPersonnelRegisterStatus(
        savedMission: DisplayableMissionDto,
        personIdList: number[]
    ): Observable<{ [personId: number]: PilotRegisterStatusUnion }> {
        const authorityIds = this.findAuthorityIds(savedMission);
        const time = savedMission.missionDate;

        if (authorityIds == null || authorityIds.length === 0 || time == null) {
            return of(
                personIdList.reduce(
                    (acc, personId) => ({
                        ...acc,
                        [personId]: null
                    }),
                    {}
                )
            );
        }

        return combineLatest(
            authorityIds.map(authId =>
                this.organisationAuthorityService
                    .findPersonnelRegisterStatuses(
                        authId,
                        personIdList,
                        time,
                        savedMission?.organisationId ?? this.organisationId
                    )
                    .pipe(
                        takeUntil(this.ngUnsubscribe$),
                        map(results => ({
                            [authId]: results.reduce(
                                (acc, result) => ({
                                    ...acc,
                                    [result.entityId]: result.registerStatus
                                }),
                                {}
                            )
                        }))
                    )
            )
        ).pipe(
            map(results =>
                results.reduce((acc, result) => ({ ...acc, ...result }), {})
            )
        );
    }

    private findMissionRegisterStatusesForPerson(
        savedMission: DisplayableMissionDto,
        personIds: number[],
        rpaIds: number[]
    ) {
        const authorityIds = this.findAuthorityIds(savedMission);
        const at = toTimestamp(
            this.missionForm.controls.missionDate.value,
            this.missionForm.controls.timeZone.value
        );
        const timeOfDay = this.missionForm.controls.timeOfDay.value;
        const visualLineOfSight =
            this.missionForm.controls.visualLineOfSight.value;
        const managingOrganisationId =
            savedMission?.organisationId ?? this.organisationId;

        if (
            authorityIds == null ||
            at == null ||
            timeOfDay == null ||
            visualLineOfSight == null ||
            managingOrganisationId == null
        ) {
            return;
        }
        this.registerEntriesOnMissionLoadingSource.next(true);
        return this.organisationAuthorityService
            .findPersonnelRegisterStatusesForMission(
                authorityIds,
                personIds,
                rpaIds,
                timeOfDay,
                visualLineOfSight,
                at,
                managingOrganisationId
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results =>
                    this.registerEntriesOnMissionSource.next(results),
                error: error => this.logging.error(error)
            })
            .add(() => this.registerEntriesOnMissionLoadingSource.next(false));
    }

    /**
     * Resolve the register status for an RPA based on a concrete mission
     * @param savedMission
     * @param rpaIdList
     */
    private findRpaRegisterStatus(
        savedMission: DisplayableMissionDto,
        rpaIdList: number[]
    ): Observable<{ [personId: number]: PilotRegisterStatusUnion }> {
        const authorityIds = this.findAuthorityIds(savedMission);
        const time = savedMission.missionDate;

        if (authorityIds == null || time == null) {
            return of(
                rpaIdList.reduce(
                    (acc, personId) => ({
                        ...acc,
                        [personId]: null
                    }),
                    {}
                )
            );
        }

        return combineLatest(
            authorityIds.map(authId =>
                this.organisationAuthorityService
                    .findRpaRegisterStatuses(
                        authId,
                        rpaIdList,
                        time,
                        savedMission?.organisationId ?? this.organisationId
                    )
                    .pipe(
                        takeUntil(this.ngUnsubscribe$),
                        map(results => ({
                            [authId]: results.reduce(
                                (acc, result) => ({
                                    ...acc,
                                    [result.entityId]: result.registerStatus
                                }),
                                {}
                            )
                        }))
                    )
            )
        ).pipe(
            map(results =>
                results.reduce((acc, result) => ({ ...acc, ...result }), {})
            )
        );
    }

    addRpa(rpaId: number | null) {
        const missionCraft = this.missionForm.controls.craftIds;
        const newCraft = new FormControl<number>(rpaId, Validators.required);
        missionCraft.push(newCraft);
        // this.setupAvailableRpa();
        missionCraft.updateValueAndValidity();
        missionCraft.markAsDirty();
    }

    removeRpa(i: number) {
        const missionCraft = this.missionForm.controls.craftIds;
        missionCraft.removeAt(i);
        missionCraft.markAsDirty();
    }

    addEquipment(equipmentId?: number) {
        const missionEquipment = this.missionForm.controls.equipmentIds;
        const newEquipment = new FormControl<number>(equipmentId);
        missionEquipment.push(newEquipment);
        // this.setupAvailableRpa();
        missionEquipment.updateValueAndValidity();
        missionEquipment.markAsDirty();
    }

    removeEquipment(ix: number) {
        const missionEquipment = this.missionForm.controls.equipmentIds;
        missionEquipment.removeAt(ix);
        missionEquipment.markAsDirty();
        if (missionEquipment.length === 0) {
            missionEquipment.setValidators(null);
            missionEquipment.updateValueAndValidity();
        }
    }

    addCrew() {
        const missionCrew = this.missionForm.controls.missionCrew;

        const crewValidator = (): ValidatorFn => {
            return (
                control: AbstractControl<MissionCrewDto>
            ): ValidationErrors | null => {
                if (
                    control.value.personId == null ||
                    control.value.missionRoleId == null
                ) {
                    return { invalidCrew: true };
                }
                return null;
            };
        };

        const newCrew = new FormControl<MissionCrewDto>(
            {
                personId: null,
                missionRoleId: null
            },
            [crewValidator()]
        );
        missionCrew.push(newCrew);
        missionCrew.updateValueAndValidity();
        missionCrew.markAsDirty();
    }

    removeCrew(ix: number) {
        const missionCrew = this.missionForm.controls.missionCrew;
        missionCrew.removeAt(ix);
        missionCrew.markAsDirty();
        if (missionCrew.length === 0) {
            missionCrew.setValidators(null);
            missionCrew.updateValueAndValidity();
        }
    }
}
